import React, { useState } from "react";
import { Diamond, Mail, Award, Palette, Sparkles, FlaskConical, GraduationCap, Droplet, TestTube, Newspaper, BookOpen } from "lucide-react";
import { Link } from "react-router-dom";
import { TopNav } from "@/components/TopNav";
import { Header } from "@/components/Header";
import { GemCard } from "@/components/GemCard";
import { FeatureStrip, AccessBanner } from "@/components/FeatureStrip";
import { SubscribeModal } from "@/components/SubscribeModal";
import { CountryFicheModal } from "@/components/CountryFicheModal";
import { CountryPickerModal } from "@/components/CountryPickerModal";
import { ClassificationModal } from "@/components/ClassificationModal";
import { ColorClassificationModal } from "@/components/ColorClassificationModal";
import { InclusionsModal } from "@/components/InclusionsModal";
import { ColorChartModal } from "@/components/ColorChartModal";
import { FourCModal } from "@/components/FourCModal";
import { Footer } from "@/components/Footer";
import { SEO } from "@/components/SEO";
import { AuthModal } from "@/components/AuthModal";
import { useLang } from "@/context/LanguageContext";
import { pageTr } from "@/i18n/pageTranslations";
import { useSubscription } from "@/context/SubscriptionContext";
import { useAuth } from "@/context/AuthContext";

const HomePage = () => {
  const { t, lang } = useLang();
  const { subscribed } = useSubscription();
  const { user } = useAuth();
  const isSubscribed = subscribed || user?.subscription_active;
  const [modalOpen, setModalOpen] = useState(false);
  const [ficheState, setFicheState] = useState({ open: false, gemType: null, country: null });
  const [pickerState, setPickerState] = useState({ open: false, gemType: null });
  const [classifOpen, setClassifOpen] = useState(false);
  const [colorOpen, setColorOpen] = useState(false);
  const [inclusionsOpen, setInclusionsOpen] = useState(false);
  const [colorChartState, setColorChartState] = useState({ open: false, gemType: null });
  const [fourCOpen, setFourCOpen] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const [authReason, setAuthReason] = useState("");
  const open = () => setModalOpen(true);
  const openFiche = (gemType) => (country) =>
    setFicheState({ open: true, gemType, country });
  const closeFiche = () => setFicheState({ open: false, gemType: null, country: null });
  const openPicker = (gemType) => setPickerState({ open: true, gemType });
  const closePicker = () => setPickerState({ open: false, gemType: null });
  const requireAuth = (reason) => { setAuthReason(reason); setAuthOpen(true); };

  return (
    <div className="min-h-screen bg-[#050811] text-[#F8F5EE] relative overflow-hidden flex flex-col">
      <SEO path="/" />
      {/* Ambient gold vignette */}
      <div
        className="pointer-events-none absolute -top-40 left-1/2 -translate-x-1/2 w-[900px] h-[500px] rounded-full blur-3xl"
        style={{ background: "radial-gradient(closest-side, rgba(193,166,97,0.10), transparent)" }}
      />

      <TopNav />
      <Header onOpenSubscribe={open} />

      {/* Hero */}
      <section className="relative px-6 md:px-14 pb-8" data-testid="hero-section">
        <div className="flex flex-col items-center text-center">
          <Diamond size={30} strokeWidth={1.2} className="text-[#C1A661] mb-3" />
          <h1
            className="font-serif font-light tracking-[0.15em] text-5xl sm:text-6xl md:text-7xl text-[#E2CFA4]"
            style={{ letterSpacing: "0.18em" }}
            data-testid="hero-title"
          >
            {t.heroTitle}
          </h1>
          <p className="mt-4 text-sm md:text-base text-[#A1A7B3] font-light tracking-wide">
            {t.heroSubtitle}
          </p>
          <p className="mt-2 text-[11px] md:text-xs text-[#C1A661] font-medium tracking-[0.3em] uppercase">
            {t.heroCriteria}
          </p>

          {/* Explore sections — always public, above the first gem card */}
          <nav
            data-testid="hero-explore-nav"
            aria-label={lang === "fr" ? "Explorer les sections" : "Explore sections"}
            className="mt-6 w-full max-w-4xl"
          >
            <div className="flex flex-wrap items-center justify-center gap-2">
              {[
                { to: "/labs", key: "labs", Icon: FlaskConical },
                { to: "/formation", key: "formation", Icon: GraduationCap },
                { to: "/treatments", key: "treatments", Icon: Droplet },
                { to: "/synthetics", key: "synthetics", Icon: TestTube },
                { to: "/journal", key: "journal", Icon: Newspaper },
              ].map(({ to, key, Icon }) => (
                <Link
                  key={key}
                  to={to}
                  data-testid={`hero-nav-${key}`}
                  className="group flex items-center gap-2 rounded-full border border-[#C1A661]/40 hover:border-[#C1A661] bg-[#080C17]/60 hover:bg-[#080C17] px-4 py-2 transition-colors"
                >
                  <Icon size={14} strokeWidth={1.4} className="text-[#C1A661] group-hover:text-[#E2CFA4]" />
                  <span className="text-[10px] md:text-[11px] tracking-[0.22em] uppercase text-[#E2CFA4]">
                    {pageTr[lang].nav[key]}
                  </span>
                </Link>
              ))}
              <button
                type="button"
                data-testid="hero-nav-fourc"
                onClick={() => setFourCOpen(true)}
                className="group flex items-center gap-2 rounded-full border border-[#C1A661]/40 hover:border-[#C1A661] bg-[#080C17]/60 hover:bg-[#080C17] px-4 py-2 transition-colors"
              >
                <BookOpen size={14} strokeWidth={1.4} className="text-[#C1A661] group-hover:text-[#E2CFA4]" />
                <span className="text-[10px] md:text-[11px] tracking-[0.22em] uppercase text-[#E2CFA4]">
                  {lang === "fr" ? "Les 4C" : "The 4Cs"}
                </span>
              </button>
            </div>
          </nav>
        </div>
      </section>

      {/* Gem cards */}
      <section className="px-6 md:px-14 pb-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-testid="gems-grid">
          <GemCard gemType="sapphire" imgSrc="/gems/sapphire.png" onOpenSubscribe={open} onOpenFiche={openFiche("sapphire")} onOpenPicker={() => openPicker("sapphire")} onOpenColorChart={() => setColorChartState({ open: true, gemType: "sapphire" })} />
          <GemCard gemType="ruby"     imgSrc="/gems/ruby.png"     onOpenSubscribe={open} onOpenFiche={openFiche("ruby")}     onOpenPicker={() => openPicker("ruby")}     onOpenColorChart={() => setColorChartState({ open: true, gemType: "ruby" })} />
          <GemCard gemType="emerald"  imgSrc="/gems/emerald.png"  onOpenSubscribe={open} onOpenFiche={openFiche("emerald")}  onOpenPicker={() => openPicker("emerald")}  onOpenColorChart={() => setColorChartState({ open: true, gemType: "emerald" })} />
        </div>
      </section>

      {/* Classification & Color buttons */}
      <section className="px-6 md:px-14 pb-8">
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
          <button
            type="button"
            data-testid="classification-open-btn"
            onClick={() => setClassifOpen(true)}
            className="group flex items-center gap-3 rounded-full border border-[#C1A661]/40 hover:border-[#C1A661] bg-[#080C17]/60 hover:bg-[#080C17] px-7 py-3.5 transition-colors"
          >
            <Award size={16} strokeWidth={1.4} className="text-[#C1A661] group-hover:text-[#E2CFA4]" />
            <div className="text-left">
              <div className="text-[11px] tracking-[0.3em] uppercase text-[#E2CFA4] font-medium">
                {lang === "fr" ? "Classification générale" : "General Classification"}
              </div>
              <div className="text-[10px] tracking-[0.2em] text-[#A1A7B3] mt-0.5">
                {lang === "fr"
                  ? "Comprendre les catégories AAA / AA / A"
                  : "Understand AAA / AA / A categories"}
              </div>
            </div>
          </button>

          <button
            type="button"
            data-testid="color-classification-open-btn"
            onClick={() => setColorOpen(true)}
            className="group flex items-center gap-3 rounded-full border border-[#C1A661]/40 hover:border-[#C1A661] bg-[#080C17]/60 hover:bg-[#080C17] px-7 py-3.5 transition-colors"
          >
            <Palette size={16} strokeWidth={1.4} className="text-[#C1A661] group-hover:text-[#E2CFA4]" />
            <div className="text-left">
              <div className="text-[11px] tracking-[0.3em] uppercase text-[#E2CFA4] font-medium">
                {lang === "fr" ? "Classification des couleurs" : "Color Classification"}
              </div>
              <div className="text-[10px] tracking-[0.2em] text-[#A1A7B3] mt-0.5">
                {lang === "fr"
                  ? "Nuances des saphirs, rubis et émeraudes"
                  : "Hues of sapphires, rubies and emeralds"}
              </div>
            </div>
          </button>

          <button
            type="button"
            data-testid="inclusions-open-btn"
            onClick={() => setInclusionsOpen(true)}
            className="group flex items-center gap-3 rounded-full border border-[#C1A661]/40 hover:border-[#C1A661] bg-[#080C17]/60 hover:bg-[#080C17] px-7 py-3.5 transition-colors"
          >
            <Sparkles size={16} strokeWidth={1.4} className="text-[#C1A661] group-hover:text-[#E2CFA4]" />
            <div className="text-left">
              <div className="text-[11px] tracking-[0.3em] uppercase text-[#E2CFA4] font-medium">
                {lang === "fr" ? "Identification : naturelle ou synthétique" : "Identification: natural or synthetic"}
              </div>
              <div className="text-[10px] tracking-[0.2em] text-[#A1A7B3] mt-0.5">
                {lang === "fr"
                  ? "Repères visuels d'inclusions typiques"
                  : "Visual markers of typical inclusions"}
              </div>
            </div>
          </button>
        </div>
      </section>

      {/* Features */}
      <section className="px-6 md:px-14 pb-6">
        <FeatureStrip />
      </section>

      {/* Diamond redirect notice — visible, informative, no diamonds on gemprice.org */}
      <section className="px-6 md:px-14 pb-6" data-testid="diamond-notice-section">
        <div
          className="block max-w-4xl mx-auto rounded-sm border-2 border-[#C1A661]/50 bg-gradient-to-r from-[#080C17]/80 via-[#0B1128]/80 to-[#080C17]/80 px-6 md:px-10 py-6 md:py-7"
        >
          <div className="flex flex-col md:flex-row items-center gap-4 md:gap-6">
            <Diamond size={36} strokeWidth={1.2} className="text-[#C1A661] shrink-0" />
            <div className="flex-1 text-center md:text-left">
              <div className="text-[10px] tracking-[0.35em] uppercase text-[#C1A661] font-medium mb-1.5">
                {lang === "fr" ? "Diamants" : "Diamonds"}
              </div>
              <p className="text-[14px] md:text-[15px] text-[#F8F5EE] font-light leading-relaxed">
                {lang === "fr"
                  ? "Pour l'information et la tarification des diamants, consultez Rapaport, le prix mondial de référence du cours du diamant."
                  : "For diamond information and pricing, please refer to Rapaport, the global reference for diamond pricing."}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Access banner — hidden for subscribers */}
      {!isSubscribed && (
        <section className="px-6 md:px-14 pb-10">
          <AccessBanner onOpenSubscribe={open} />
        </section>
      )}

      {/* SEO content section — visible, indexable copy naming labs and origins */}
      <section
        className="px-6 md:px-14 pb-6"
        data-testid="seo-content-section"
        aria-labelledby="seo-content-title"
      >
        <div className="max-w-4xl mx-auto border border-[#C1A661]/20 rounded-sm bg-[#080C17]/60 px-6 md:px-10 py-8 md:py-10">
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#C1A661] mb-3">
            {lang === "fr" ? "Standards de référence" : "Reference standards"}
          </div>
          <h2
            id="seo-content-title"
            className="font-serif text-2xl md:text-3xl text-[#E2CFA4] font-light tracking-wide mb-4"
          >
            {lang === "fr"
              ? "Saphirs, rubis et émeraudes : les tarifs alignés sur les laboratoires GIA, IGI, SSEF, Gübelin, AGL, GRS Gemsearch et les grands salons (Tucson Gem & Mineral Show)"
              : "Sapphires, rubies and emeralds: pricing aligned with GIA, IGI, SSEF, Gübelin, AGL, GRS Gemsearch laboratories and the major shows (Tucson Gem & Mineral Show)"}
          </h2>
          <div className="space-y-3 text-[13px] md:text-sm text-[#D8DBE2] font-light leading-relaxed">
            <p>
              {lang === "fr"
                ? "GEM PRICE propose une grille tarifaire des pierres précieuses au carat, structurée selon les référentiels des grands laboratoires internationaux : GIA (Gemological Institute of America), IGI (International Gemological Institute), SSEF (Swiss Gemmological Institute), Gübelin Gem Lab, AGL (American Gemological Laboratories) et GRS Gemsearch (Gemresearch Swisslab). Les cotations sont recoupées avec les tendances observées lors des grands salons professionnels : Tucson Gem & Mineral Show, AGTA GemFair Tucson, ainsi que les mineral shows internationaux (Munich, Sainte-Marie-aux-Mines, Hong Kong)."
                : "GEM PRICE publishes a per-carat pricing grid for precious gemstones structured around the frameworks of leading international laboratories: GIA (Gemological Institute of America), IGI (International Gemological Institute), SSEF (Swiss Gemmological Institute), Gübelin Gem Lab, AGL (American Gemological Laboratories) and GRS Gemsearch (Gemresearch Swisslab). Prices are cross-referenced with trends observed at major professional shows: Tucson Gem & Mineral Show, AGTA GemFair Tucson, and international mineral shows (Munich, Sainte-Marie-aux-Mines, Hong Kong)."}
            </p>
            <p>
              {lang === "fr"
                ? "Chaque fiche couvre une pierre et une provenance : saphirs du Cachemire, de Ceylan (Sri Lanka), de Birmanie (Myanmar), de Madagascar ou d'Australie ; rubis de Birmanie, du Mozambique, de Tanzanie ou de Thaïlande ; émeraudes de Colombie, de Zambie, d'Éthiopie ou du Brésil. Les qualités AAA, AA et A sont documentées, ainsi que les traitements courants (chauffage des saphirs, huilage des émeraudes) et les principaux synthétiques (Verneuil, Chatham, Chatham hydrothermal, Kyocera, Kashan)."
                : "Each fact sheet covers one gem from a single origin: Kashmir, Ceylon (Sri Lanka), Burma (Myanmar), Madagascar or Australian sapphires; Burmese, Mozambican, Tanzanian or Thai rubies; Colombian, Zambian, Ethiopian or Brazilian emeralds. AAA, AA and A qualities are documented, along with common treatments (heated sapphires, oiled emeralds) and the main synthetics (Verneuil, Chatham, Chatham hydrothermal, Kyocera, Kashan)."}
            </p>
            <p>
              {lang === "fr"
                ? "Les fiches d'identification visuelle des inclusions aident à distinguer les pierres naturelles, chauffées et synthétiques. Le journal mensuel de gemmologie relaie les publications récentes du GIA (Gems & Gemology), des mines Kagem (Gemfields), les rapports GRS Gemsearch et Gübelin, ainsi que les tendances du Tucson Gem & Mineral Show et des principaux mineral shows internationaux."
                : "Visual identification sheets of inclusions help distinguish natural, heated and synthetic stones. Our monthly gemology journal covers recent publications from GIA (Gems & Gemology), Kagem mines (Gemfields), GRS Gemsearch and Gübelin reports, plus trends from the Tucson Gem & Mineral Show and major international mineral shows."}
            </p>
          </div>
        </div>
      </section>

      {/* Trust & Sources section — outbound links to authoritative labs + guarantees */}
      <section
        className="px-6 md:px-14 pb-10"
        data-testid="trust-section"
        aria-labelledby="trust-title"
      >
        <div className="max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Sources & Standards */}
          <div className="border border-[#C1A661]/25 rounded-sm bg-[#080C17]/60 p-6 md:p-7">
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#C1A661] mb-2">
              {lang === "fr" ? "Sources officielles" : "Official sources"}
            </div>
            <h2
              id="trust-title"
              className="font-serif text-xl md:text-2xl text-[#E2CFA4] font-light mb-3"
            >
              {lang === "fr"
                ? "Standards laboratoires reconnus"
                : "Recognised laboratory standards"}
            </h2>
            <p className="text-[13px] text-[#D8DBE2] font-light leading-relaxed mb-4">
              {lang === "fr"
                ? "Nos données croisent les publications et référentiels des principaux laboratoires internationaux :"
                : "Our data cross-references publications and frameworks from the leading international laboratories:"}
            </p>
            <ul className="grid grid-cols-2 gap-2 text-[12px]">
              {[
                ["GIA", "https://www.gia.edu/"],
                ["IGI", "https://www.igi.org/"],
                ["SSEF", "https://www.ssef.ch/"],
                ["Gübelin", "https://www.gubelingemlab.com/"],
                ["AGL", "https://www.aglgemlab.com/"],
                ["GRS Gemsearch", "https://www.gemresearch.ch/"],
                ["Lotus Gemology", "https://www.lotusgemology.com/"],
                ["Gemfields", "https://gemfields.com/"],
              ].map(([name, url]) => (
                <li key={name}>
                  <a
                    href={url}
                    target="_blank"
                    rel="noopener external"
                    data-testid={`source-link-${name.toLowerCase().replace(/\s+/g, "-")}`}
                    className="flex items-center gap-2 text-[#A1A7B3] hover:text-[#E2CFA4] transition-colors underline-offset-2 hover:underline"
                  >
                    <span className="text-[#C1A661]">→</span>
                    <span>{name}</span>
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Guarantees */}
          <div className="border border-[#C1A661]/25 rounded-sm bg-[#080C17]/60 p-6 md:p-7">
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#C1A661] mb-2">
              {lang === "fr" ? "Vos garanties" : "Your guarantees"}
            </div>
            <h2 className="font-serif text-xl md:text-2xl text-[#E2CFA4] font-light mb-3">
              {lang === "fr" ? "Un site sécurisé et conforme" : "A secure and compliant site"}
            </h2>
            <ul className="space-y-2.5 text-[13px] text-[#D8DBE2] font-light">
              {(lang === "fr" ? [
                "Paiement sécurisé par Stripe (norme PCI-DSS)",
                "Connexion chiffrée HTTPS sur toutes les pages",
                "Données personnelles conformes RGPD (UE)",
                "Résiliation possible à tout moment depuis votre profil",
                "Support client bilingue français/anglais",
                "Sources et méthodologie publiques",
              ] : [
                "Secure payment by Stripe (PCI-DSS compliant)",
                "Encrypted HTTPS connection on every page",
                "Personal data GDPR-compliant (EU)",
                "Cancel anytime from your profile",
                "Bilingual French/English customer support",
                "Public sources and methodology",
              ]).map((line, i) => (
                <li key={i} className="flex gap-2">
                  <span className="text-[#C1A661] shrink-0">✓</span>
                  <span>{line}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Contact banner */}
      <section className="px-6 md:px-14 pb-6" data-testid="contact-section">
        <div className="max-w-3xl mx-auto text-center border border-[#C1A661]/25 rounded-sm bg-[#080C17]/60 px-8 py-10">
          <Mail size={22} strokeWidth={1.3} className="text-[#C1A661] mx-auto mb-3" />
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#C1A661] mb-2">
            {t.contact.eyebrow}
          </div>
          <h2 className="font-serif text-2xl md:text-3xl text-[#E2CFA4] tracking-wide mb-3">
            {t.contact.title}
          </h2>
          <p className="text-sm text-[#A1A7B3] font-light max-w-xl mx-auto mb-5">
            {t.contact.body}
          </p>
          <a
            href="mailto:gemprice@gemprice.org"
            data-testid="home-contact-email"
            className="inline-flex items-center gap-2.5 text-[#E2CFA4] hover:text-[#C1A661] transition-colors font-serif text-lg md:text-xl tracking-wide"
          >
            <Mail size={16} strokeWidth={1.4} className="text-[#C1A661]" />
            gemprice@gemprice.org
          </a>
        </div>
      </section>

      {/* JewelDesigner cross-link */}
      <section className="px-6 md:px-14 pb-10 flex justify-center">
        <a
          href="https://jeweldesigner.app"
          target="_blank"
          rel="noopener external"
          data-testid="home-jeweldesigner-link"
          className="inline-flex items-center gap-2 rounded-full border border-[#C1A661]/40 hover:border-[#C1A661] bg-[#080C17]/60 hover:bg-[#080C17] px-6 py-2.5 text-[11px] tracking-[0.3em] uppercase text-[#E2CFA4] hover:text-[#F8F5EE] transition-colors"
        >
          jeweldesigner.app ↗
        </a>
      </section>

      <footer className="px-6 md:px-14 pb-8 text-center text-[10px] tracking-[0.3em] uppercase text-[#8A8F99]/70">
        {t.footer}
      </footer>

      <Footer />

      <SubscribeModal open={modalOpen} onClose={() => setModalOpen(false)} onRequireAuth={requireAuth} />
      <AuthModal open={authOpen} onClose={() => setAuthOpen(false)} reason={authReason} />

      <CountryFicheModal
        open={ficheState.open}
        onClose={closeFiche}
        gemType={ficheState.gemType}
        country={ficheState.country}
        onSubscribe={() => { closeFiche(); open(); }}
      />

      <CountryPickerModal
        open={pickerState.open}
        onClose={closePicker}
        gemType={pickerState.gemType}
        onSelectCountry={(country) => {
          setPickerState({ open: false, gemType: null });
          setFicheState({ open: true, gemType: pickerState.gemType, country });
        }}
      />

      <ClassificationModal
        open={classifOpen}
        onClose={() => setClassifOpen(false)}
        onSubscribe={() => { setClassifOpen(false); open(); }}
      />

      <ColorClassificationModal
        open={colorOpen}
        onClose={() => setColorOpen(false)}
        onSubscribe={() => { setColorOpen(false); open(); }}
      />

      <InclusionsModal
        open={inclusionsOpen}
        onClose={() => setInclusionsOpen(false)}
        onSubscribe={() => { setInclusionsOpen(false); open(); }}
      />

      <ColorChartModal
        open={colorChartState.open}
        gemType={colorChartState.gemType}
        onClose={() => setColorChartState({ open: false, gemType: null })}
      />

      <FourCModal
        open={fourCOpen}
        onClose={() => setFourCOpen(false)}
      />

      {/* Floating persistent subscribe button (bottom-right) — hidden for subscribers */}
      {!modalOpen && !isSubscribed && (
        <button
          data-testid="subscribe-floating-btn"
          onClick={open}
          className="fixed bottom-4 right-4 md:bottom-6 md:right-6 z-40 flex items-center gap-2 md:gap-2.5 rounded-full bg-[#C1A661] hover:bg-[#E2CFA4] px-4 md:px-6 py-3 md:py-3.5 text-[10px] md:text-xs tracking-[0.18em] md:tracking-[0.22em] uppercase text-[#050B1E] font-semibold shadow-[0_10px_40px_rgba(193,166,97,0.35)] transition-colors duration-200 max-w-[calc(100vw-2rem)]"
          style={{ paddingBottom: "max(0.75rem, env(safe-area-inset-bottom))" }}
        >
          <Diamond size={14} strokeWidth={1.6} />
          {t.subscribeFloatingCta}
        </button>
      )}
    </div>
  );
};

export default HomePage;
